/**
 * ProgressButton - Circular button with progress ring
 *
 * Shows completion progress for the onboarding checklist with an
 * animated ring indicator and pulsating orange glow when incomplete.
 */

import { ClipboardCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProgressButtonProps {
  completed: number;
  total: number;
  onClick: () => void;
  isActive?: boolean;
}

export function ProgressButton({ completed, total, onClick, isActive }: ProgressButtonProps) {
  const progress = total > 0 ? (completed / total) * 100 : 0;
  const isComplete = completed >= total;

  // SVG circle parameters
  const size = 56;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <button
      onClick={onClick}
      className={cn(
        'relative w-14 h-14 rounded-full bg-white border transition-all duration-300',
        'hover:scale-105',
        isActive ? 'border-amber-400' : 'border-amber-200',
        isComplete && 'border-green-300 shadow-lg',
        // Pulsating orange glow when incomplete
        !isComplete && 'animate-glow-pulse'
      )}
      style={!isComplete ? {
        animation: 'glow-pulse 2s ease-in-out infinite',
      } : undefined}
      aria-label={`Getting started progress: ${completed} of ${total} complete`}
    >
      {/* Inline keyframes for pulsating glow */}
      {!isComplete && (
        <style>{`
          @keyframes glow-pulse {
            0%, 100% {
              box-shadow: 0 0 15px 3px rgba(249, 115, 22, 0.4),
                          0 0 30px 6px rgba(249, 115, 22, 0.2),
                          0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }
            50% {
              box-shadow: 0 0 25px 8px rgba(249, 115, 22, 0.6),
                          0 0 50px 15px rgba(249, 115, 22, 0.3),
                          0 10px 15px -3px rgba(0, 0, 0, 0.1);
            }
          }
        `}</style>
      )}

      {/* Progress Ring SVG */}
      <svg
        className="absolute inset-0 w-full h-full -rotate-90"
        viewBox={`0 0 ${size} ${size}`}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#fef3c7"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={isComplete ? '#22c55e' : '#f59e0b'}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-500 ease-out"
        />
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <ClipboardCheck className={cn(
          'h-5 w-5',
          isComplete ? 'text-green-600' : 'text-amber-600'
        )} />
        <span className={cn(
          'text-[10px] font-semibold',
          isComplete ? 'text-green-700' : 'text-amber-700'
        )}>
          {completed}/{total}
        </span>
      </div>
    </button>
  );
}

export default ProgressButton;
